/**
 * faceSheet — pure assembly for the Face Sheet (WS-4 D5), the founder's
 * "Emergency Folder" one-pager: everything a hospital needs about a
 * client on one printable page.
 *
 * NO new data enters here — every function derives from the ONE
 * `ClientRecordSnapshot` the record page already fetched (one audited
 * read; the sheet adds no fetch and no decrypt path). Derivations that
 * exist elsewhere are mirrored exactly, never re-invented:
 *   - next appointment = the HeroFacts rule (active + scheduled, soonest
 *     scheduledFor, dateText-only rows sort last);
 *   - home and currently-at = src/lib/clientLocation.ts's homeAddress
 *     and currentlyAt, the same two rules the Contact band and the
 *     Addresses card read;
 *   - allergies = the AllergiesCard's three states (recorded list /
 *     confirmed-none / not-recorded — empty NEVER means allergy-free).
 */

import type {
  AllergyRow,
  ClientPlace,
  ClientPronouns,
  ClientRecordSnapshot,
  ConditionRow,
  ContactRow,
  CoverageRow,
  FunctionalStatusRow,
  HospitalEventRow,
  MedicationRow,
  ProviderRow,
  RiskFlagRow,
  VitalsBand,
} from '@/lib/clientRecord';
import { sortAllergiesBySeverity } from '@/lib/allergySort';
import { byStayNewestFirst } from '@/lib/hospitalEvents';
import { CONTACT_ROLE_KEYS, contactRoleWord, type ContactRoleKey } from '@/lib/fieldLabels';
import { orderedCareProviders } from '@/lib/careProviders';
import { formatPhoneDisplay } from '@/lib/phone';
import { isCurrentMedication, medicationTiming } from '@/lib/medicationTiming';

// ---------------------------------------------------------------------
// Addresses: the sheet's Home and Currently at lines read
// `@/lib/clientLocation` (homeAddress, currentlyAt) and render through
// `@/lib/address` (formatAddress), like every surface that shows a
// client address as text.
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Demographics.
// ---------------------------------------------------------------------

/** Whole-year age on `todayIso` (both 'YYYY-MM-DD'; lexicographic-safe). */
export function ageOn(dobIso: string, todayIso: string): number {
  const dobMonthDay = dobIso.slice(5);
  const todayMonthDay = todayIso.slice(5);
  const years = Number(todayIso.slice(0, 4)) - Number(dobIso.slice(0, 4));
  return todayMonthDay >= dobMonthDay ? years : years - 1;
}

// ---------------------------------------------------------------------
// Contacts: ER-relevant ordering + the role line.
// ---------------------------------------------------------------------

/**
 * Active contacts, the people an ER calls first on top: emergency
 * contacts, then POA/health-care-proxy holders, then the rest (stable
 * within each rank — the record's own order).
 */
export function faceSheetContacts(contacts: ContactRow[]): ContactRow[] {
  const rank = (c: ContactRow): number =>
    c.isEmergency ? 0 : c.isPoa || c.isHcp || c.isGuardian ? 1 : 2;
  return contacts
    .filter((c) => c.isActive)
    .map((c, i) => [c, i] as const)
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1])
    .map(([c]) => c);
}

/**
 * The contact's roles in the one map's words and the contact form's order
 * (CONTACT_ROLE_KEYS), "Lives with" naming the client by pronoun. Empty
 * string when the contact carries no flags.
 */
export function contactRolesLine(c: ContactRow, pronouns: ClientPronouns | null = null): string {
  const on: Record<ContactRoleKey, boolean> = {
    is_emergency: c.isEmergency,
    lives_with: c.livesWith,
    is_poa: c.isPoa,
    is_hcp: c.isHcp,
    is_guardian: c.isGuardian,
    is_billing: c.isBilling,
  };
  return CONTACT_ROLE_KEYS.filter((k) => on[k])
    .map((k) => contactRoleWord(k, pronouns))
    .join(' · ');
}

// ---------------------------------------------------------------------
// Care team: active rows in the care team's order; office phone/address
// with the directory fallback (the WS-5/0140 display rule).
// ---------------------------------------------------------------------

export function faceSheetProviders(providers: ProviderRow[]): ProviderRow[] {
  // The care team's order everywhere: not in the Directory, then people,
  // then organizations, each A to Z (one list here, no sub-headings).
  return orderedCareProviders(providers);
}

export function providerPhone(p: ProviderRow): string | null {
  // Soak #2 #20: format for display (a clean US number renders pretty;
  // extensions / non-US strings pass through unchanged).
  const raw = p.officePhone ?? p.directoryPhone ?? null;
  return raw ? formatPhoneDisplay(raw) : null;
}

export function providerAddress(p: ProviderRow): string | null {
  return p.officeAddress ?? p.directoryAddress ?? null;
}

/** The row's own email (0231), else its Directory entry's. */
export function providerEmail(p: ProviderRow): string | null {
  return p.email ?? p.directoryEmail ?? null;
}

// ---------------------------------------------------------------------
// Insurance + medications: active rows only (a hospital needs the
// current truth; archived history stays on the record).
// ---------------------------------------------------------------------

export function faceSheetCoverage(coverage: CoverageRow[]): CoverageRow[] {
  return coverage.filter((c) => c.isActive);
}

/** The medications the client takes today (LP-41): one starting later or
 *  stopped by its date is not on the sheet. `today` is the practice day. */
export function faceSheetMedications(medications: MedicationRow[], today: string): MedicationRow[] {
  return medications.filter((m) => isCurrentMedication(m, today));
}

// ---------------------------------------------------------------------
// QRF v1: the sections the sheet omitted, and the client's history. Every
// list reads the one snapshot, and an archived row (removed by a person)
// prints nowhere.
// ---------------------------------------------------------------------

const RISK_ORDER: Record<string, number> = { elevated: 0, watch: 1 };

/** Risk flags that are not resolved, elevated before watch. */
export function faceSheetRiskFlags(rows: RiskFlagRow[]): RiskFlagRow[] {
  return rows
    .filter((r) => r.isActive && r.level !== 'resolved')
    .sort((a, b) => (RISK_ORDER[a.level] ?? 2) - (RISK_ORDER[b.level] ?? 2));
}

/** Conditions in force: active and watched. */
export function faceSheetConditions(rows: ConditionRow[]): ConditionRow[] {
  return rows.filter((c) => c.isActive && c.status !== 'resolved');
}

/** Assessed activities only: a not_assessed row says nothing on paper. */
export function faceSheetFunctional(rows: FunctionalStatusRow[]): FunctionalStatusRow[] {
  return rows.filter((f) => f.isActive && f.level !== 'not_assessed');
}

const newestFirst = (a?: string, b?: string): number => (b ?? '').localeCompare(a ?? '');

/** The Quick Reference's hospital events: every one not archived, dated
 *  ones newest first, then those known only by their words. */
export function qrfHospitalEvents(rows: HospitalEventRow[]): HospitalEventRow[] {
  return rows
    .filter((h) => h.isActive)
    .sort(byStayNewestFirst);
}

/** The Quick Reference: resolved conditions, newest noted first. */
export function faceSheetResolvedConditions(rows: ConditionRow[]): ConditionRow[] {
  return rows.filter((c) => c.isActive && c.status === 'resolved').sort((a, b) => newestFirst(a.notedOn, b.notedOn));
}

/** The Quick Reference: stopped medications (discontinued, or past their
 *  stop date, LP-41), the most recently stopped first. */
export function faceSheetPastMedications(rows: MedicationRow[], today: string): MedicationRow[] {
  return rows
    .filter((m) => m.isActive && medicationTiming(m, today) === 'stopped')
    .sort((a, b) => newestFirst(a.endedOn ?? a.startedOn, b.endedOn ?? b.startedOn));
}

// ---------------------------------------------------------------------
// Allergies — the AllergiesCard's three honest states.
// ---------------------------------------------------------------------

export type FaceSheetAllergies =
  | { kind: 'recorded'; rows: AllergyRow[] }
  | { kind: 'none_known'; confirmedOn: string }
  | { kind: 'not_recorded' };

export function faceSheetAllergies(record: ClientRecordSnapshot): FaceSheetAllergies {
  const rows = record.allergies.filter((a) => a.isActive);
  // Most severe first, then alphabetical — the ONE order every allergy
  // list on the record shares (src/lib/allergySort.ts); this sheet
  // prints, so a stranger reading it in an ER sees the same ranking a
  // CM sees on the record itself.
  if (rows.length > 0) return { kind: 'recorded', rows: sortAllergiesBySeverity(rows) };
  if (record.allergiesNoneKnownOn !== null) {
    return { kind: 'none_known', confirmedOn: record.allergiesNoneKnownOn };
  }
  return { kind: 'not_recorded' };
}

// ---------------------------------------------------------------------
// LP-33: the places on the client, and the Client history's status lines.
// ---------------------------------------------------------------------

/** A place's one line for paper: the name, then its phone formatted when
 *  the Directory knows one (a typed place has none). Null for none. */
export function placeLine(place: ClientPlace | null): string | null {
  if (place === null) return null;
  return place.phone !== null ? `${place.name} · ${formatPhoneDisplay(place.phone)}` : place.name;
}

/** The sheet's advance-directive words: which of POLST, DPOA and the
 *  health care proxy are on file, in that order; empty when none is. */
export function faceSheetDirectives(record: ClientRecordSnapshot): string[] {
  const onFile = (instrument: string): boolean =>
    record.legalInstruments.some((l) => l.isActive && l.instrument === instrument && l.status === 'on_file');
  return [
    onFile('polst') ? 'POLST on file' : null,
    onFile('dpoa') ? 'DPOA (durable power of attorney) on file' : null,
    onFile('healthcare_proxy') ? 'Health care proxy on file' : null,
  ].filter((s): s is string => s !== null);
}

/** One line of the Client history's Status changes section. `date` is
 *  'YYYY-MM-DD' or null for the standing status. */
export interface HistoryStatusLine {
  key: string;
  date: string | null;
  text: string;
}

/**
 * The Client history's status changes, from the record's own facts: the
 * standing status first (with its note, as the header shows it) when it
 * is a change from the start, that is anything but Active with no note;
 * then the dated events newest first: the date of death, the hospice
 * start and the service start. An active client with none of those gives
 * an empty list, so the section reads None recorded.
 */
export function clientHistoryStatusLines(vitals: VitalsBand, proseDay: (iso: string) => string): HistoryStatusLine[] {
  const dated: HistoryStatusLine[] = [];
  if (vitals.dateOfDeath !== null) {
    dated.push({ key: 'death', date: vitals.dateOfDeath, text: `Date of death ${proseDay(vitals.dateOfDeath)}` });
  }
  if (vitals.onHospiceSince !== null) {
    dated.push({ key: 'hospice', date: vitals.onHospiceSince, text: `On hospice since ${proseDay(vitals.onHospiceSince)}` });
  }
  if (vitals.serviceStartDate !== null) {
    dated.push({ key: 'service', date: vitals.serviceStartDate, text: `Service started ${proseDay(vitals.serviceStartDate)}` });
  }
  dated.sort((a, b) => newestFirst(a.date ?? undefined, b.date ?? undefined));
  const changedFromStart = vitals.lifecycleStatus !== 'active' || vitals.statusNote !== null || !vitals.isActive;
  const standing: HistoryStatusLine[] =
    changedFromStart && vitals.status !== null ? [{ key: 'status', date: null, text: `Status: ${vitals.status}` }] : [];
  return [...standing, ...dated];
}
